import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';
import logiwaOrderService from '../services/logiwaOrderService';

/**
 * Inline badge showing the Logiwa sync state of a truck.
 *  - link is null    -> render nothing (non-LTL or no Logiwa match)
 *  - both pushed, no errors -> green "Logiwa: synced"
 *  - any error              -> red "Logiwa: failed"
 *  - link exists, no pushes yet -> yellow "Logiwa: linked"
 *
 * Fetches its own link via GET /:id/logiwa-link on mount. Safe on non-LTL trucks
 * because the server returns { link: null } for trucks with no row.
 */
const LogiwaSyncBadge = ({ truckId, refreshKey }) => {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    logiwaOrderService
      .getLogiwaLink(truckId)
      .then((res) => { if (!cancelled) setLink(res && res.link ? res.link : null); })
      .catch(() => { if (!cancelled) setLink(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [truckId, refreshKey]);

  if (loading) {
    return (
      <span className="inline-flex items-center text-xs text-slate-500">
        <Loader className="w-3 h-3 mr-1 animate-spin" />
      </span>
    );
  }
  if (!link) return null;

  // A deferred ship isn't a failure — it means the warehouse hasn't advanced the order to a
  // shippable state yet. Amber "linked" is the right read for that, not red "failed".
  const shipDeferred = !!(link.logiwa_ship_error && link.logiwa_ship_error.startsWith('[deferred] '));
  // Ship and billing errors previously went unnoticed here — a failed /ship or an unposted
  // billing charge still rendered green "synced". Both now count.
  const hasError = !!(
    link.logiwa_update_error ||
    link.logiwa_document_error ||
    (link.logiwa_ship_error && !shipDeferred) ||
    link.logiwa_vas_error
  );
  // "Synced" still keys off update + document only. Deliberately NOT requiring ship/vas pushed_at:
  // those columns are null on rows that predate their respective migrations, and on trucks where
  // the billing push is disabled server-side — requiring them would flip historical green badges
  // to amber for reasons unrelated to anything actually being wrong.
  const fullySynced = !!(link.logiwa_update_pushed_at && link.logiwa_document_pushed_at) && !hasError;

  if (hasError) {
    return (
      <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-red-900/40 border border-red-800 text-red-200">
        <AlertCircle className="w-3 h-3 mr-1" />
        Logiwa: failed
      </span>
    );
  }
  if (fullySynced) {
    return (
      <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-green-900/40 border border-green-800 text-green-200">
        <CheckCircle className="w-3 h-3 mr-1" />
        Logiwa: synced
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-amber-900/40 border border-amber-800 text-amber-200">
      Logiwa: linked
    </span>
  );
};

export default LogiwaSyncBadge;
